import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import BusinessIcon from '@material-ui/icons/Business';
import ReceiptIcon from '@material-ui/icons/Receipt';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Section, SectionHeading } from '../Common/PageLayout';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: tokens.white,
        borderRadius: tokens.radius,
        padding: theme.spacing(4),
        color: tokens.ink,
    },
    icon: {
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        backgroundColor: tokens.blue50,
        color: tokens.blue700,
        marginBottom: theme.spacing(2.5),
    },
    title: {
        marginBottom: theme.spacing(1.5),
        color: tokens.ink,
    },
    description: {
        color: tokens.inkSoft,
        fontSize: '0.9375rem',
        lineHeight: 1.65,
        margin: `0 0 ${theme.spacing(3)}px`,
        flexGrow: 1,
    },
    action: { alignSelf: 'flex-start' },
}));

const items = [
    {
        title: 'Find open opportunities',
        description:
            'Browse current tender notices, pre-qualifications and requests for quotation published by the Ministry and its directorates.',
        icon: <SearchIcon />,
        label: 'View open tenders',
        to: '/tenders',
    },
    {
        title: 'Register as a supplier',
        description:
            'Suppliers must register on the national e-Procurement Portal before quoting for government goods, works or services.',
        icon: <BusinessIcon />,
        label: 'e-Procurement Portal',
        href: 'http://www.eprocurement.gov.tl/',
    },
    {
        title: 'Procurement and payments',
        description:
            'Understand how the Ministry authorises and executes payments, and where budget execution is published.',
        icon: <ReceiptIcon />,
        label: 'Procurement information',
        to: '/procurementpage',
    },
];

/** Procurement entry points for suppliers and contractors. */
export default function PublicProcurement() {
    const classes = useStyles();

    return (
        <Section tone="brand">
            <SectionHeading
                onBrand
                overline="For suppliers"
                title="Public procurement"
                description="Government contracts are awarded through open competition. Tender notices are published here and on the national e-Procurement Portal, where suppliers register and submit bids."
                action={{ label: 'Tender archive', to: '/tenders_archive' }}
            />

            <Grid container spacing={4}>
                {items.map((item) => (
                    <Grid item xs={12} md={4} key={item.title}>
                        <div className={classes.card}>
                            <span className={classes.icon}>{item.icon}</span>

                            <Typography variant="h5" component="h3" className={classes.title}>
                                {item.title}
                            </Typography>

                            <p className={classes.description}>{item.description}</p>

                            {item.href ? (
                                <Button
                                    className={classes.action}
                                    variant="contained"
                                    color="primary"
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    endIcon={<OpenInNewIcon />}
                                >
                                    {item.label}
                                </Button>
                            ) : (
                                <Button
                                    className={classes.action}
                                    variant="contained"
                                    color="primary"
                                    component={RouterLink}
                                    to={item.to}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    {item.label}
                                </Button>
                            )}
                        </div>
                    </Grid>
                ))}
            </Grid>
        </Section>
    );
}
